/**
 * Capture and normalize drawing-board SVG for thumbnails and saved quotes.
 * Strips foreignObject HTML (editable dimension inputs) — invalid in standalone SVG/XML.
 */

import {
  ensureBottomDimensionClearance,
  stripPartSegmentWidthRulers,
  stripSvgDimensionLabels,
  repositionHeightPartLabelsBetweenRulers,
} from './drawingDimensionOverlays';

const SVG_NS = 'http://www.w3.org/2000/svg';

const stripForeignObjectsRaw = (svgHtml) =>
  svgHtml.replace(/<foreignObject\b[^>]*>[\s\S]*?<\/foreignObject>/gi, '');

const stripMmUnit = (label) => String(label).replace(/\s*mm\s*$/i, '').trim();

const extractForeignObjectLabel = (fo) => {
  const input = fo.querySelector('input');
  if (input) {
    const value = input.value ?? input.getAttribute('value') ?? '';
    return stripMmUnit(String(value));
  }

  const spans = Array.from(fo.querySelectorAll('span'))
    .map((span) => span.textContent?.trim())
    .filter(Boolean)
    .filter((text) => text.toLowerCase() !== 'mm');
  if (spans.length) return stripMmUnit(spans.join(' '));

  return stripMmUnit(fo.textContent?.trim() || '');
};

const readInlineStyle = (el, prop, fallback) => {
  const style = el?.getAttribute?.('style') || el?.style?.cssText || '';
  const match = style.match(new RegExp(`${prop}\\s*:\\s*([^;]+)`, 'i'));
  return match?.[1]?.trim() || fallback;
};

const isPerPartSegmentLabel = (fo) => {
  const w = parseFloat(fo.getAttribute('width') || 0);
  return w > 0 && w <= 55;
};

/** True height rulers sit to the RIGHT of the outer frame — not over the glass.
 *  Right-half overhead/door width part labels (e.g. second "700") must stay width labels. */
const isRightSideHeightForeignObject = (fo) => {
  const svg = fo.ownerSVGElement || fo.closest?.('svg');
  if (!svg || !fo) return false;

  const x = parseFloat(fo.getAttribute('x') || '0');
  const y = parseFloat(fo.getAttribute('y') || '0');
  const w = parseFloat(fo.getAttribute('width') || '0');
  if (!(w > 0 && w <= 90)) return false;

  const vb = svg.viewBox?.baseVal;
  const vbX = vb?.x || 0;
  const vbW = vb?.width || parseFloat(svg.getAttribute('width')) || 900;

  let frameRight = null;
  let frameTop = null;
  let frameBottom = null;
  svg.querySelectorAll('rect').forEach((rect) => {
    const rw = parseFloat(rect.getAttribute('width') || '0');
    const rh = parseFloat(rect.getAttribute('height') || '0');
    const rx = parseFloat(rect.getAttribute('x') || '0');
    const ry = parseFloat(rect.getAttribute('y') || '0');
    if (rw < 60 || rh < 60) return;
    if (rw >= vbW * 0.92) return;
    const fill = (rect.getAttribute('fill') || '').toLowerCase();
    if (fill === '#f5f5f5') return;
    const right = rx + rw;
    const bottom = ry + rh;
    if (frameRight == null || right > frameRight) frameRight = right;
    if (frameTop == null || ry < frameTop) frameTop = ry;
    if (frameBottom == null || bottom > frameBottom) frameBottom = bottom;
  });

  // Must start at/after the frame's right edge (height gutter), not over the sash/glass
  if (frameRight != null && x + 2 < frameRight) return false;

  // Near the top of the frame = overhead horizontal width labels, never height
  if (frameTop != null && y < frameTop + 10) return false;

  // Below the sill = overall / part width band
  if (frameBottom != null && y > frameBottom + 6) return false;

  // Still require being on the right side of the canvas
  if (x <= vbX + vbW * 0.45) return false;

  return true;
};

/** Side-light height labels sit LEFT of the outer frame (achterdeur / terrasdeur zijlicht). */
const isLeftSideHeightForeignObject = (fo) => {
  if (!fo) return false;
  if (fo.getAttribute('data-height-side') === 'left') return true;
  if (fo.getAttribute('data-dim-axis') === 'height' && fo.getAttribute('data-height-side') !== 'right') {
    const x = parseFloat(fo.getAttribute('x') || '0');
    const svg = fo.ownerSVGElement || fo.closest?.('svg');
    const vb = svg?.viewBox?.baseVal;
    const vbX = vb?.x || 0;
    const vbW = vb?.width || parseFloat(svg?.getAttribute('width')) || 900;
    if (x < vbX + vbW * 0.45) return true;
  }

  const svg = fo.ownerSVGElement || fo.closest?.('svg');
  if (!svg) return false;

  const x = parseFloat(fo.getAttribute('x') || '0');
  const y = parseFloat(fo.getAttribute('y') || '0');
  const w = parseFloat(fo.getAttribute('width') || '0');
  if (!(w > 0 && w <= 90)) return false;

  const vb = svg.viewBox?.baseVal;
  const vbX = vb?.x || 0;
  const vbW = vb?.width || parseFloat(svg.getAttribute('width')) || 900;

  let frameLeft = null;
  let frameTop = null;
  let frameBottom = null;
  svg.querySelectorAll('rect').forEach((rect) => {
    const rw = parseFloat(rect.getAttribute('width') || '0');
    const rh = parseFloat(rect.getAttribute('height') || '0');
    const rx = parseFloat(rect.getAttribute('x') || '0');
    const ry = parseFloat(rect.getAttribute('y') || '0');
    if (rw < 60 || rh < 60) return;
    if (rw >= vbW * 0.92) return;
    const fill = (rect.getAttribute('fill') || '').toLowerCase();
    if (fill === '#f5f5f5') return;
    const bottom = ry + rh;
    if (frameLeft == null || rx < frameLeft) frameLeft = rx;
    if (frameTop == null || ry < frameTop) frameTop = ry;
    if (frameBottom == null || bottom > frameBottom) frameBottom = bottom;
  });

  // Must end at/before the frame's left edge (left height gutter)
  if (frameLeft != null && x + w > frameLeft + 4) return false;

  if (frameTop != null && y < frameTop + 10) return false;
  if (frameBottom != null && y > frameBottom + 6) return false;

  if (x >= vbX + vbW * 0.45) return false;

  return true;
};

const isDimensionLabelText = (textEl) => {
  if (textEl.getAttribute('data-dimension-label') === 'true') return true;
  const content = textEl.textContent?.trim() || '';
  if (!content || content.length === 1) return false;
  return /\d/.test(content);
};

/** Read SVG viewBox size (user units). */
const getSvgViewBoxSize = (svg) => {
  if (!svg) return { width: 800, height: 800 };
  const vb = svg.viewBox?.baseVal;
  if (vb?.width > 0 && vb?.height > 0) {
    return { width: vb.width, height: vb.height };
  }
  const attr = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/);
  if (attr.length === 4) {
    const width = parseFloat(attr[2]);
    const height = parseFloat(attr[3]);
    if (width > 0 && height > 0) return { width, height };
  }
  const width = parseFloat(svg.getAttribute('width')) || 800;
  const height = parseFloat(svg.getAttribute('height')) || 800;
  return { width, height };
};

/**
 * Convert a desired on-screen px size into SVG user units.
 * Quote drawings are typically shown ~280–360px tall; without this scale-up,
 * 12–16 unit labels become ~4–6px and look invisible.
 */
const screenPxToSvgUnits = (svg, screenPx) => {
  const { height } = getSvgViewBoxSize(svg);
  const assumedDisplayHeight = 320;
  return Math.round(Math.max(16, screenPx * (height / assumedDisplayHeight)));
};

const resolveEmbedFontSize = (svg, { perPart = false, labelCount = 1 } = {}) => {
  // Match reference drawings: medium, readable — not oversized/heavy
  const preferredScreenPx = perPart ? 11 : 12.5;
  let size = screenPxToSvgUnits(svg, preferredScreenPx);
  if (!perPart && labelCount > 4) size = Math.round(size * 0.92);
  return size;
};

const styleDimensionLabel = (textEl, fontSize) => {
  textEl.setAttribute('font-size', String(fontSize));
  textEl.setAttribute('font-weight', '500');
  textEl.setAttribute('font-family', 'Arial, Helvetica, sans-serif');
  textEl.setAttribute('fill', '#2C2C2C');
  // No stroke/halo — that filled digit counters and looked bold/blurry in PDF
  textEl.removeAttribute('stroke');
  textEl.removeAttribute('stroke-width');
  textEl.removeAttribute('paint-order');
  textEl.setAttribute('data-dimension-label', 'true');
};

/** Push horizontal labels below the dimension line (reference style). */
const clearLabelFromDimensionLine = (textEl, fontSize, axis) => {
  if (textEl.getAttribute('data-dim-nudge') === '1') return;

  if (axis === 'height') {
    textEl.setAttribute('data-dim-nudge', '1');
    textEl.setAttribute('data-dim-axis', 'height');
    return;
  }

  const y = parseFloat(textEl.getAttribute('y') || '0');
  // Sit just under the ruler with a small gap (like the reference drawing)
  textEl.setAttribute('y', String(y + Math.max(6, fontSize * 0.42)));
  textEl.setAttribute('dominant-baseline', 'hanging');
  textEl.setAttribute('data-dim-nudge', '1');
  textEl.setAttribute('data-dim-axis', 'width');
};

const cleanupLegacyLabelBackgrounds = (svg) => {
  svg.querySelectorAll('rect[data-dimension-label-bg="true"]').forEach((rect) => rect.remove());
};

const enhanceDimensionLabelsForEmbed = (svg) => {
  if (!svg) return;

  cleanupLegacyLabelBackgrounds(svg);

  const dimensionTexts = Array.from(svg.querySelectorAll('text')).filter(isDimensionLabelText);
  const labelCount = dimensionTexts.length;
  const mainFontSize = resolveEmbedFontSize(svg, { perPart: false, labelCount });
  const partFontSize = resolveEmbedFontSize(svg, { perPart: true, labelCount });

  dimensionTexts.forEach((textEl) => {
    const stripped = stripMmUnit(textEl.textContent?.trim() || '');
    if (stripped) textEl.textContent = stripped;

    const foWidth = parseFloat(textEl.getAttribute('data-fo-width') || '0');
    const axisAttr = textEl.getAttribute('data-dim-axis');
    const anchor = textEl.getAttribute('text-anchor') || 'middle';
    const perPart = foWidth > 0 && foWidth <= 55;
    const axis = axisAttr || (anchor === 'start' ? 'height' : 'width');

    let fontSize = parseFloat(textEl.getAttribute('font-size') || '0');
    if (fontSize < 16 || fontSize > 56) {
      fontSize = perPart ? partFontSize : mainFontSize;
    } else if (textEl.getAttribute('stroke')) {
      // Previously stroked/bold labels — rebuild at a clean readable size
      fontSize = perPart ? partFontSize : mainFontSize;
    }

    styleDimensionLabel(textEl, fontSize);
    clearLabelFromDimensionLine(textEl, fontSize, axis);
  });
};

export const replaceForeignObjectsWithText = (svgRoot) => {
  if (!svgRoot) return;

  const foreignObjects = Array.from(svgRoot.querySelectorAll('foreignObject'));
  const mainLabels = foreignObjects.filter((fo) => !isPerPartSegmentLabel(fo));
  const mainFontSize = resolveEmbedFontSize(svgRoot, {
    perPart: false,
    labelCount: Math.max(1, mainLabels.length),
  });
  const partFontSize = resolveEmbedFontSize(svgRoot, {
    perPart: true,
    labelCount: foreignObjects.length,
  });

  const foreignObjectToText = (fo) => {
    const label = extractForeignObjectLabel(fo);
    if (!label) return null;

    const x = parseFloat(fo.getAttribute('x') || 0);
    const y = parseFloat(fo.getAttribute('y') || 0);
    const w = parseFloat(fo.getAttribute('width') || 0);
    const h = parseFloat(fo.getAttribute('height') || 0);
    const perPart = isPerPartSegmentLabel(fo);
    const input = fo.querySelector('input');
    const inputAlign = input ? readInlineStyle(input, 'text-align', 'center') : 'center';
    const anchor = inputAlign === 'left' ? 'start' : 'middle';
    const fontSize = perPart ? partFontSize : mainFontSize;
    // Section height labels: right of frame, OR left side-light height (achterdeur)
    const isLeftHeight = isLeftSideHeightForeignObject(fo);
    const isHeight =
      fo.getAttribute('data-dim-axis') === 'height' ||
      anchor === 'start' ||
      isRightSideHeightForeignObject(fo) ||
      isLeftHeight;

    const textEl = document.createElementNS(SVG_NS, 'text');
    textEl.setAttribute('text-anchor', isLeftHeight ? 'end' : isHeight ? 'start' : anchor);
    textEl.setAttribute('fill', '#2C2C2C');
    textEl.textContent = label;
    if (perPart) textEl.setAttribute('data-fo-width', String(w));

    if (isHeight) {
      // Height labels: right of vertical ruler, or left of left-side light ruler
      if (isLeftHeight) {
        textEl.setAttribute('x', String(x + w - 4));
        textEl.setAttribute('data-height-side', 'left');
      } else {
        textEl.setAttribute('x', String(x + 2));
      }
      textEl.setAttribute('y', String(y + h / 2));
      textEl.setAttribute('dominant-baseline', 'middle');
      textEl.setAttribute('data-dim-axis', 'height');
      textEl.setAttribute('data-dim-nudge', '1');
      if (w > 0 && w <= 90) {
        textEl.setAttribute('data-height-part', '1');
      }
    } else if (perPart || w <= 55) {
      // Horizontal pane width labels — keep all parts on the same side of the ruler.
      // Top-band (overhead) labels sit above; bottom-band labels sit on the FO center.
      const vb = svgRoot.viewBox?.baseVal;
      const vbY = vb?.y || 0;
      const vbH = vb?.height || parseFloat(svgRoot.getAttribute('height')) || 800;
      const isTopBand =
        fo.getAttribute('data-dim-band') === 'top' || y < vbY + vbH * 0.38;
      textEl.setAttribute('x', String(x + w / 2));
      if (isTopBand) {
        textEl.setAttribute('y', String(Math.max(vbY + 2, y + Math.min(4, h * 0.35))));
        textEl.setAttribute('dominant-baseline', 'auto');
        textEl.setAttribute('data-dim-band', 'top');
      } else {
        textEl.setAttribute('y', String(y + h * 0.55));
        textEl.setAttribute('dominant-baseline', 'middle');
      }
      textEl.setAttribute('data-dim-axis', 'width');
      textEl.setAttribute('data-dim-nudge', '1');
    } else {
      // Overall width — FO is already below the main ruler
      textEl.setAttribute('x', String(x + w / 2));
      textEl.setAttribute('y', String(y + h * 0.55));
      textEl.setAttribute('dominant-baseline', 'middle');
      textEl.setAttribute('data-dim-axis', 'width');
      textEl.setAttribute('data-dim-nudge', '1');
    }

    styleDimensionLabel(textEl, fontSize);
    return textEl;
  };

  foreignObjects.forEach((fo) => {
    const textEl = foreignObjectToText(fo);
    if (textEl) {
      fo.replaceWith(textEl);
    } else {
      fo.remove();
    }
  });
};

const GRAY_CANVAS_FILL = /^#f5f5f5$/i;

/** Light frame edge strokes that disappear on white quote/PDF paper. */
const LIGHT_FRAME_EDGE_STROKE =
  /^(#d0d4da|#e5e7eb|#e0e0e0|#d1d5db|#cccccc|#c0c0c0|#dee2e6)$/i;
/** Existing printable/dark frame edges that are still too thin vs dimension rulers (1.5). */
const THIN_DARK_FRAME_EDGE_STROKE = /^(#1f2937|#2c2c2c|#374151|#4a4a4a|#111827)$/i;

const QUOTE_FRAME_EDGE_STROKE = '#2C2C2C';
const QUOTE_FRAME_EDGE_WIDTH = '2';
const QUOTE_DIM_STROKE = '#2C2C2C';
const QUOTE_DIM_WIDTH = '2.25';
const DIMENSION_LINE_STROKE =
  /^(#2c2c2c|#4a4a4a|#1f2937|#374151|#111827)$/i;

/**
 * Make outer/inner frame outlines as visible as dimension rulers on Export Quote.
 * Live configurator keeps its thinner strokes; this only runs for saved quote SVGs.
 */
const enhanceFrameEdgeVisibilityForQuote = (svg) => {
  if (!svg) return;

  const readStrokeWidth = (el) => {
    const raw = el.getAttribute('stroke-width') ?? el.getAttribute('strokeWidth');
    if (raw == null || raw === '') return null;
    const n = parseFloat(raw);
    return Number.isNaN(n) ? null : n;
  };

  const hardenStroke = (el, stroke) => {
    if (LIGHT_FRAME_EDGE_STROKE.test(stroke)) {
      el.setAttribute('stroke', QUOTE_FRAME_EDGE_STROKE);
      const width = readStrokeWidth(el);
      if (width == null || width < 2) {
        el.setAttribute('stroke-width', QUOTE_FRAME_EDGE_WIDTH);
      }
      return;
    }
    if (THIN_DARK_FRAME_EDGE_STROKE.test(stroke)) {
      const width = readStrokeWidth(el);
      if (width != null && width > 0 && width < 2) {
        el.setAttribute('stroke-width', QUOTE_FRAME_EDGE_WIDTH);
      }
    }
  };

  svg.querySelectorAll('line, rect, path, polyline, polygon').forEach((el) => {
    const stroke = (el.getAttribute('stroke') || '').trim();
    if (!stroke || stroke.toLowerCase() === 'none') return;
    hardenStroke(el, stroke);
  });

  // Printable edge groups set stroke on <g>; children often only set stroke-width
  svg.querySelectorAll('g[stroke]').forEach((group) => {
    const groupStroke = (group.getAttribute('stroke') || '').trim();
    if (!groupStroke || groupStroke.toLowerCase() === 'none') return;

    if (LIGHT_FRAME_EDGE_STROKE.test(groupStroke)) {
      group.setAttribute('stroke', QUOTE_FRAME_EDGE_STROKE);
    }

    const effectiveDark =
      THIN_DARK_FRAME_EDGE_STROKE.test(groupStroke) ||
      LIGHT_FRAME_EDGE_STROKE.test(groupStroke) ||
      groupStroke.toUpperCase() === QUOTE_FRAME_EDGE_STROKE;

    if (!effectiveDark) return;

    group.querySelectorAll('line, rect, path, polyline, polygon').forEach((el) => {
      const ownStroke = (el.getAttribute('stroke') || '').trim();
      if (ownStroke && ownStroke.toLowerCase() !== 'none') return;
      const width = readStrokeWidth(el);
      if (width != null && width > 0 && width < 2) {
        el.setAttribute('stroke-width', QUOTE_FRAME_EDGE_WIDTH);
      }
    });
  });
};

/**
 * Quote/PDF: merge broken vertical sub-ruler segments into one solid column
 * and thicken all dimension strokes so they match the main 2200/1000 rulers.
 *
 * Only merges nearly contiguous segments (small Y gap). Never bridges a large
 * gap — e.g. overhead + underlight mullion edges at the same X must not become
 * full-height lines through the main glass pane.
 */
const unifyAndBoostDimensionRulersForQuote = (svg) => {
  if (!svg) return;

  const vb = svg.viewBox?.baseVal;
  const vbX = vb?.x || 0;
  const vbH = vb?.height || parseFloat(svg.getAttribute('height')) || 700;
  const vbW = vb?.width || parseFloat(svg.getAttribute('width')) || 900;
  const minX = vbX + vbW * 0.42;
  // Broken dim ticks sit a few px apart; main glass between overhead/underlight is much larger
  const maxMergeGap = Math.max(16, Math.min(32, vbH * 0.035));

  const verticalSegs = [];
  svg.querySelectorAll('line').forEach((line) => {
    const x1 = parseFloat(line.getAttribute('x1') || 'NaN');
    const x2 = parseFloat(line.getAttribute('x2') || 'NaN');
    const y1 = parseFloat(line.getAttribute('y1') || 'NaN');
    const y2 = parseFloat(line.getAttribute('y2') || 'NaN');
    if ([x1, x2, y1, y2].some((n) => Number.isNaN(n))) return;
    if (Math.abs(x1 - x2) > 0.75) return;
    const len = Math.abs(y2 - y1);
    if (len < 28) return;
    if (x1 < minX) return;

    const stroke = (line.getAttribute('stroke') || '').trim();
    // Dimension rulers only — never treat light mullion/frame edge strokes as rulers
    if (stroke && !DIMENSION_LINE_STROKE.test(stroke)) {
      return;
    }

    verticalSegs.push({
      line,
      x: x1,
      yMin: Math.min(y1, y2),
      yMax: Math.max(y1, y2),
    });
  });

  // Cluster by X, then merge only contiguous groups within each column
  verticalSegs.sort((a, b) => a.x - b.x || a.yMin - b.yMin);
  const clusters = [];
  verticalSegs.forEach((seg) => {
    const last = clusters[clusters.length - 1];
    if (!last || Math.abs(seg.x - last.x) > 6) {
      clusters.push({ x: seg.x, segs: [seg] });
    } else {
      last.segs.push(seg);
    }
  });

  const mergeContiguousGroup = (group, x) => {
    if (group.length < 2) return;
    const yMin = Math.min(...group.map((s) => s.yMin));
    const yMax = Math.max(...group.map((s) => s.yMax));
    const parent = group[0].line.parentNode || svg;
    const merged = document.createElementNS(SVG_NS, 'line');
    merged.setAttribute('x1', String(x));
    merged.setAttribute('x2', String(x));
    merged.setAttribute('y1', String(yMin));
    merged.setAttribute('y2', String(yMax));
    merged.setAttribute('stroke', QUOTE_DIM_STROKE);
    merged.setAttribute('stroke-width', QUOTE_DIM_WIDTH);
    merged.setAttribute('stroke-linecap', 'square');
    parent.insertBefore(merged, group[0].line);
    group.forEach((s) => s.line.remove());
  };

  clusters.forEach((cluster) => {
    if (cluster.segs.length < 2) return;
    const sorted = [...cluster.segs].sort((a, b) => a.yMin - b.yMin);
    let group = [sorted[0]];
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = group[group.length - 1];
      const gap = sorted[i].yMin - prev.yMax;
      if (gap <= maxMergeGap) {
        group.push(sorted[i]);
      } else {
        mergeContiguousGroup(group, cluster.x);
        group = [sorted[i]];
      }
    }
    mergeContiguousGroup(group, cluster.x);
  });

  // Thicken remaining dimension lines + ticks (horizontal and vertical)
  svg.querySelectorAll('line').forEach((line) => {
    const stroke = (line.getAttribute('stroke') || '').trim();
    if (!stroke || !DIMENSION_LINE_STROKE.test(stroke)) return;
    line.setAttribute('stroke', QUOTE_DIM_STROKE);
    line.setAttribute('stroke-width', QUOTE_DIM_WIDTH);
    line.setAttribute('stroke-linecap', 'square');
  });
};

const applyCanvasBackground = (svg, color) => {
  if (!svg) return;
  svg.querySelectorAll('rect').forEach((rect) => {
    const fill = rect.getAttribute('fill');
    if (fill && GRAY_CANVAS_FILL.test(fill)) {
      rect.setAttribute('fill', color);
    }
  });
};

const normalizeSvgElement = (svg, { saved = false } = {}) => {
  if (!svg) return null;

  ensureBottomDimensionClearance(svg, { minGap: 40, minPartMainGap: 52 });
  stripPartSegmentWidthRulers(svg);
  replaceForeignObjectsWithText(svg);
  enhanceDimensionLabelsForEmbed(svg);
  if (saved) {
    // Unify dimension rulers BEFORE hardening light frame/mullion edges to dark,
    // so overhead/underlight mullion edges are not mistaken for broken height rulers.
    unifyAndBoostDimensionRulersForQuote(svg);
    enhanceFrameEdgeVisibilityForQuote(svg);
  }
  repositionHeightPartLabelsBetweenRulers(svg);
  applyCanvasBackground(svg, saved ? '#FFFFFF' : 'none');
  svg.removeAttribute('style');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  if (!svg.getAttribute('xmlns')) {
    svg.setAttribute('xmlns', SVG_NS);
  }

  return svg;
};

const serializeSvg = (svg) => new XMLSerializer().serializeToString(svg);

const XLINK_NS = 'http://www.w3.org/1999/xlink';

const resolveSvgImageUrl = (href) => {
  if (!href || href.startsWith('data:')) return null;
  if (href.startsWith('http://') || href.startsWith('https://')) return href;
  const base = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  if (href.startsWith('/')) return `${base}${href}`;
  return `${base}/${href}`;
};

/** Inline external SVG <image> hrefs as data URLs (required for iOS Safari + PDF export). */
export const inlineSvgExternalImages = async (svgHtml) => {
  if (!svgHtml || typeof document === 'undefined') return svgHtml;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgHtml, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg) return svgHtml;

    const images = Array.from(svg.querySelectorAll('image'));
    const cache = {};

    await Promise.all(
      images.map(async (img) => {
        const href = img.getAttribute('href') || img.getAttributeNS(XLINK_NS, 'href');
        const fullUrl = resolveSvgImageUrl(href);
        if (!fullUrl) return;

        if (!cache[fullUrl]) {
          try {
            const resp = await fetch(fullUrl);
            const blob = await resp.blob();
            cache[fullUrl] = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch {
            return;
          }
        }

        img.setAttribute('href', cache[fullUrl]);
        try {
          img.setAttributeNS(XLINK_NS, 'href', cache[fullUrl]);
        } catch {
          // ignore xlink set failures
        }
      })
    );

    return serializeSvg(svg);
  } catch {
    return svgHtml;
  }
};

export const stripDimensionLabelsFromSvgHtml = (svgHtml) => {
  if (!svgHtml || typeof document === 'undefined') return svgHtml;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgHtml, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg) return svgHtml;

    stripSvgDimensionLabels(svg);
    return serializeSvg(svg);
  } catch {
    return svgHtml;
  }
};

export const prepareSvgHtmlForDisplay = async (
  svgHtml,
  { saved = true, stripDimensionLabels = false } = {}
) => {
  let prepared = prepareSvgHtmlForEmbed(svgHtml, { saved });
  if (!prepared) return null;

  if (stripDimensionLabels) {
    prepared = stripDimensionLabelsFromSvgHtml(prepared);
  }

  return inlineSvgExternalImages(prepared);
};

const applyBackgroundToSvgHtml = (svgHtml, saved) =>
  svgHtml.replace(/\bfill="#[fF]5[fF]5[fF]5"/g, saved ? 'fill="#FFFFFF"' : 'fill="none"');

export const prepareSvgHtmlForEmbed = (svgHtml, { saved = false } = {}) => {
  if (!svgHtml || typeof svgHtml !== 'string') return null;

  try {
    const parser = new DOMParser();
    let doc = parser.parseFromString(svgHtml, 'image/svg+xml');
    let parseError = doc.querySelector('parsererror');
    let svg = doc.querySelector('svg');

    if (!svg || parseError) {
      const cleaned = stripForeignObjectsRaw(svgHtml);
      doc = parser.parseFromString(cleaned, 'image/svg+xml');
      parseError = doc.querySelector('parsererror');
      svg = doc.querySelector('svg');
    }

    if (svg) {
      return serializeSvg(normalizeSvgElement(svg, { saved }));
    }
  } catch {
    // fall through to regex cleanup
  }

  return applyBackgroundToSvgHtml(
    stripForeignObjectsRaw(svgHtml)
      .replace(/\sstyle="[^"]*"/gi, '')
      .replace(/<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"')
      .replace(/\swidth="[^"]*"/i, ' width="100%"')
      .replace(/\sheight="[^"]*"/i, ' height="100%"'),
    saved
  );
};

export const svgHtmlToDataUrl = (svgHtml, options = {}) => {
  const prepared = options.alreadyPrepared
    ? svgHtml
    : prepareSvgHtmlForEmbed(svgHtml, options);
  if (!prepared) return null;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(prepared)}`;
};

const findMainSvg = (root) => {
  if (!root) return null;
  if (root.tagName === 'svg') return root;

  const svgs = Array.from(root.querySelectorAll('svg'));
  if (!svgs.length) return null;

  let best = svgs[0];
  let bestScore = 0;

  svgs.forEach((svg) => {
    const rect = svg.getBoundingClientRect();
    const score = rect.width * rect.height;
    const viewBox = svg.getAttribute('viewBox');
    const viewScore = viewBox
      ? viewBox.split(/\s+/).slice(2).reduce((a, b) => a * parseFloat(b || 0), 1)
      : 0;
    const finalScore = score > 0 ? score : viewScore;
    if (finalScore > bestScore) {
      bestScore = finalScore;
      best = svg;
    }
  });

  return best;
};

const syncForeignObjectInputValues = (sourceSvg, targetSvg) => {
  const sourceObjects = sourceSvg.querySelectorAll('foreignObject');
  const targetObjects = targetSvg.querySelectorAll('foreignObject');
  sourceObjects.forEach((sourceFo, index) => {
    const targetFo = targetObjects[index];
    if (!targetFo) return;
    const sourceInput = sourceFo.querySelector('input');
    const targetInput = targetFo.querySelector('input');
    if (sourceInput && targetInput) {
      targetInput.value = sourceInput.value;
    }
  });
};

export const captureDrawingSvg = (drawingBoardRef, { saved = false } = {}) => {
  const root = drawingBoardRef?.current;
  const svgElement = findMainSvg(root);
  if (!svgElement) return null;

  const clone = svgElement.cloneNode(true);
  syncForeignObjectInputValues(svgElement, clone);
  const rawHtml = new XMLSerializer().serializeToString(clone);
  return prepareSvgHtmlForEmbed(rawHtml, { saved });
};
